// CourseProgressOverlay.jsx

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated,
} from 'react-native-web';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useDispatch, useSelector } from 'react-redux';
import {
  startOneOnOneMeeting,
  stopOneOnOneMeeting,
} from 'store/communication/actions/speechToTextServiceActions';
import World from 'components/World';


// Blinking Icon component remains unchanged
const BlinkingIcon = () => {
  const blinkAnim = useRef(new Animated.Value(1)).current;
  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(blinkAnim, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(blinkAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  }, [blinkAnim]);
  return (
    <Animated.View style={{ opacity: blinkAnim }}>
      <Icon name="circle" size={10} color="#FFF" style={{ marginRight: 4 }} />
    </Animated.View>
  );
};

// Helper to format course title as multiline (aligned right)
const formatCourseTitle = title => {
  const regex = /^(.*?)(\s*\(.*\))$/;
  const match = title.match(regex);
  if (match) {
    return (
      <Text style={styles.courseTitle}>
        {`${match[1].trim()}\n${match[2].trim()}`}
      </Text>
    );
  }
  return <Text style={styles.courseTitle}>{title}</Text>;
};

const CourseProgressOverlay = () => {
  const dispatch = useDispatch();
  const { courseData, loading, error } = useSelector(state => state.course);
  const ready_to_move = useSelector(
    state => state.meeting.meeting.ready_to_move,
  );
  const token = useSelector(state => state.meeting.properties.token);

  const [unitTrackingLogged, setUnitTrackingLogged] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [hoveredUnitId, setHoveredUnitId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState(true);
  const [oneToOneMeetingMode, setOneToOneMeetingMode] = useState(false);
  const [expandedOverlay, setExpandedOverlay] = useState(true);
  
  const toggleAll = () => {
    setExpandedOverlay(!expandedOverlay);
  };
  
    useEffect(() => {
      const units = courseData?.units;
    if (units && units.length > 0 && expandedId === null) {
     const studyingUnit = units.find(unit => unit.isCurrentlyStudying);

    if (studyingUnit) {
      setExpandedId(studyingUnit.id);
    }
    }
  }, [courseData]); 

  // Measure container height so we can position the button below it
  const [containerHeight, setContainerHeight] = useState(0);

  // Button pulsing animation
  const buttonAnim = useRef(new Animated.Value(0)).current;
  const [btnHovered, setBtnHovered] = useState(false);
  const [closeBtnHovered, setCloseBtnHovered] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);
  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );
  React.useEffect(() => {
    setOneToOneMeetingMode(oneToOneMeetingStatus)
  }, [oneToOneMeetingStatus]);

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(buttonAnim, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.timing(buttonAnim, { toValue: 0, duration: 500, useNativeDriver: true }),
      ]),
    ).start();
  }, [buttonAnim]);
  const pulseScale = buttonAnim.interpolate({
    inputRange: [!oneToOneMeetingMode ? 0 : 1, 1],
    outputRange: [1, !oneToOneMeetingMode ? 1.1 : 1],
  });

  const toggleOneToOneMeetingMode = () => {
    setOneToOneMeetingMode(!oneToOneMeetingMode);
    World.runCommand(`onetoone ${!oneToOneMeetingMode}`);
    console.log('toggleOneToOneMeetingMode from golden button', oneToOneMeetingMode);
    if (!oneToOneMeetingMode) {
      dispatch(startOneOnOneMeeting());
    } else {
      dispatch(stopOneOnOneMeeting());
    }
  };

  if (!ready_to_move) return null;

  if (loading) {
    return (
      <View
        style={[styles.container, { opacity: isHovered ? 1 : 0.4 }]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text style={{ color: '#FFF', padding: 12 }}>Loading...</Text>
      </View>
    );
  }

  if (error || !courseData) {
    return (
      <View
        style={[styles.container, { opacity: isHovered ? 1 : 0.4 }]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Text style={{ color: '#FFF', padding: 12 }}>{error}</Text>
      </View>
    );
  }

  const { courseTitle, units, courseMastery } = courseData;



  const toggleUnit = unitId => {
    setExpandedId(prev => (prev === unitId ? null : unitId));
  };

  const getStatusBadge = unit => {
    const renderMasteryBadge = () => {
      if (unit.unitMasteryPercentage >= 95) {
        return (
          <View
            style={[styles.badge, { backgroundColor: '#f59e0b', marginLeft: 4 }]}
          >
            <Text style={styles.badgeText}>
              Expert {unit.unitMasteryPercentage}%
            </Text>
          </View>
        );
      } else if (unit.unitMasteryPercentage >= 90) {
        return (
          <View
            style={[styles.badge, { backgroundColor: '#065f46', marginLeft: 4 }]}
          >
            <Text style={styles.badgeText}>
              Mastered {unit.unitMasteryPercentage}%
            </Text>
          </View>
        );
      } else if (
        unit.unitMasteryPercentage === 0 &&
        unit.lessonsCompletedPercentage === 0
      ) {
        return (
          <View
            style={[styles.badge, { backgroundColor: '#fca5a5', marginLeft: 4 }]}
          >
            <Text style={styles.badgeText}>Not started</Text>
          </View>
        );
      }
      return (
        <Text style={[styles.statusDefault, { marginLeft: 4 }]}>
          {unit.unitMasteryPercentage}%
        </Text>
      );
    };

    if (unit.isCurrentlyStudying) {
      return (
        <View style={styles.badgeGroup}>
          <View
            style={[styles.badge, { backgroundColor: '#DA3F3F', marginLeft: 0 }]}
          >
            <BlinkingIcon />
            <Text style={styles.badgeText}>Current</Text>
          </View>
          {renderMasteryBadge()}
        </View>
      );
    }

    return renderMasteryBadge();
  };

  const overallMastery = courseMastery;

  return (
    <>
      {!oneToOneMeetingMode && <View
        style={[styles.container, { opacity: isHovered ? 1 : 0.4 }]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onLayout={e => setContainerHeight(e.nativeEvent.layout.height)}
      >
        <View style={styles.overlay}>
          {/* Header */}
          <TouchableOpacity
            style={styles.header}
            activeOpacity={0.7}
            onPress={toggleAll}
          >
            <Text style={styles.headerTitle}>Course Progress</Text>
            <View style={{ alignItems: 'flex-end' }}>
              {formatCourseTitle(courseTitle)}
              <View style={styles.courseProgressContainer}>
                <Text style={styles.progressLabel}>Course Mastery</Text>
                <Text style={styles.progressValue}>{overallMastery}%</Text>
              </View>
              <View style={styles.progressBarOuter}>
                <View
                  style={[styles.progressBarInner, { width: `${overallMastery}%` }]}
                />
              </View>
            </View>
                      <Icon
                        name={expandedOverlay ? 'chevron-up' : 'chevron-down'}
                        size={16}
                        color="#BDDE36"
                        style={{ marginLeft: 6 }}
                      />
          </TouchableOpacity>

          {/* Units */}
          <ScrollView style={[
                    styles.unitsContainer,
                    expandedOverlay
                      ? styles.unitsContainerExpanded
                      : styles.unitsContainerCollapsed,
                  ]}>
            {units.map(unit => {
              const isExpanded = unit.id === expandedId;
              return (
                <View key={unit.id} style={styles.unitWrapper}>
                  <TouchableOpacity
                    activeOpacity={1}
                    style={[
                      styles.unitHeader,
                      hoveredRowId === unit.id && styles.unitHeaderHover,
                    ]}
                    onPress={() => toggleUnit(unit.id)}
                    onMouseEnter={() => setHoveredRowId(unit.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    <View style={styles.unitHeaderLeft}>
                      <Text style={styles.unitTitle}>
                        <Text style={{ fontWeight: 'bold' }}>
                          Unit {unit.unitNumber}:{' '}
                        </Text>
                        {unit.title}
                      </Text>
                      <View
                        onMouseEnter={e => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.bottom + 5,
                            left: rect.left + rect.width / 2 - 110,
                          });
                          setHoveredUnitId(unit.id);
                        }}
                        onMouseLeave={() => setHoveredUnitId(null)}
                        style={{ position: 'relative', marginHorizontal: 2 }}
                      >
                        <Icon
                          name="information-outline"
                          size={14}
                          color="#BDDE36"
                        />
                      </View>
                      {hoveredUnitId === unit.id &&
                        ReactDOM.createPortal(
                          <View
                            style={[
                              styles.tooltip,
                              {
                                top: tooltipPosition.top,
                                left: tooltipPosition.left,
                              },
                            ]}
                          >
                            <View style={styles.tooltipRow}>
                              <Text style={styles.tooltipLabel}>
                                Total Lessons:
                              </Text>
                              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Icon name="book-outline" size={14} color="#BDDE36" />
                                <Text style={[styles.tooltipValue, { marginLeft: 4 }]}>
                                  {unit.totalLessons}
                                </Text>
                              </View>
                            </View>
                            <View style={styles.tooltipRow}>
                              <Text style={styles.tooltipLabel}>
                                100% Completed:
                              </Text>
                              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Icon
                                  name="check-circle-outline"
                                  size={14}
                                  color="#BDDE36"
                                />
                                <Text style={[styles.tooltipValue, { marginLeft: 4 }]}>
                                  {unit.hundredPercentCompletedCount}
                                </Text>
                              </View>
                            </View>
                          </View>,
                          document.body,
                        )}
                    </View>
                    <View style={styles.unitHeaderRight}>
                      {getStatusBadge(unit)}
                      <Icon
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={14}
                        color="#BDDE36"
                        style={{ marginLeft: 8 }}
                      />
                    </View>
                  </TouchableOpacity>
                  <View
                    style={[
                      styles.expandedContent,
                      isExpanded ? styles.expanded : styles.collapsed,
                    ]}
                  >
                    <View style={styles.progressRow}>
                      <Text style={styles.progressRowLabel}>Unit Mastery</Text>
                      <Text style={styles.progressRowValue}>
                        {unit.unitMasteryPercentage}%
                      </Text>
                    </View>
                    <View style={styles.smallBarOuter}>
                      <View
                        style={[
                          styles.smallBarInner,
                          { width: `${unit.unitMasteryPercentage}%` },
                        ]}
                      />
                    </View>
                    <View style={styles.progressRow}>
                      <Text style={styles.progressRowLabel}>
                        Lessons Completed
                      </Text>
                      <Text style={styles.progressRowValue}>
                        {unit.lessonsCompletedPercentage}%
                      </Text>
                    </View>
                    <View style={styles.smallBarOuter}>
                      <View
                        style={[
                          styles.smallBarInner,
                          { width: `${unit.lessonsCompletedPercentage}%` },
                        ]}
                      />
                    </View>
                    <View style={styles.dummy} />
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
      </View>}
      <View style={[styles.container]}>
        {/* Golden Call‐to‐Action Button */}
        <Animated.View
          style={{
                        position: 'absolute',
            top: !oneToOneMeetingMode ? 16 + containerHeight + 8 : 8,
            left: '0%',          // ← center point of viewport

            transform: [
              { scale: pulseScale },
              { scale: btnHovered ? 1.2 : 1 },
              { scale: btnPressed ? 0.9 : 1 },
            ],
          }}
        >
          <TouchableOpacity
            activeOpacity={1}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            onPress={() => {
              console.log('Clicked');
              toggleOneToOneMeetingMode();
            }}
            style={styles.goldenButton}
          >
            {!oneToOneMeetingMode ? (
              <View style={styles.goldenButtonTextContainer}>
                <Text style={[styles.goldenButtonText, styles.centeredText]}>
                  CHAT WITH BEN FRANKLIN NOW!
                </Text>
                <Text
                  style={[styles.goldenButtonSecondaryText, styles.centeredText]}
                >
                  Enter 1-to-1 Tutor mode
                </Text>
              </View>
            ) : (
              <Text style={[styles.goldenButtonText, styles.centeredText]}>
                END CHAT
              </Text>
            )}
          </TouchableOpacity>
        </Animated.View>
      </View>
    </>
  );
};

export default CourseProgressOverlay;

const styles = StyleSheet.create({
  /* Container & Overlay */
  container: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 50,
    minWidth: 400,
    borderRadius: 8,
    shadowColor: 'rgba(197,232,55,1)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 15,
    elevation: 20,
    marginLeft: -200,
  },
  overlay: {
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderColor: 'rgba(189,222,54,0.3)',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  /* Golden Button */
  goldenButton: {
    backgroundColor: '#FFD700',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: '#e6c200',
    shadowColor: '#FFD700',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.7,
    shadowRadius: 10,
    elevation: 10,
  },
  goldenButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    textShadowColor: 'rgba(0,0,0,0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  goldenButtonTextContainer: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  goldenButtonSecondaryText: {
    color: '#000',
    fontSize: 14,
    marginTop: 2,
  },
  container: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 50,
    minWidth: 400,
    borderRadius: 8,
    shadowColor: 'rgba(197,232,55,1)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 15,
    elevation: 20,
  },
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderColor: 'rgba(189,222,54,0.3)',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  /* Header */
  header: {
    backgroundColor: 'rgba(189,222,54,0.1)',
    borderBottomColor: 'rgba(189,222,54,0.2)',
    borderBottomWidth: 1,
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontWeight: 'bold',
    color: '#BDDE36',
    fontSize: 14,
  },
  courseTitle: {
    color: '#BDDE36',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'right',
  },
  courseProgressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    width: 130,
  },
  progressLabel: {
    fontSize: 9,
    color: '#a1a1a1',
  },
  progressValue: {
    fontSize: 9,
    color: '#BDDE36',
  },
  progressBarOuter: {
    width: 130,
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.3)',
    marginTop: 3,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
  },
   unitsContainer: {
   overflow: 'hidden',
   transition: 'max-height 0.3s linear',
 },
 unitsContainerExpanded: {
   maxHeight: 360,
 },
 unitsContainerCollapsed: {
   maxHeight: 0,
 },
  unitWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(189,222,54,0.2)',
  },
  unitHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    transition: 'background-color 0.3s ease',
    justifyContent: 'space-between',
  },
  unitHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  unitHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  unitHeaderHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
  unitTitle: {
    fontSize: 12,
    color: '#FFF',
    marginRight: 8,
    flexShrink: 1,
  },
  /* Expand/Collapse */
  expandedContent: {
    width: '90%',
    alignSelf: 'center',
    overflow: 'hidden',
    transition: 'max-height 0.5s linear',
  },
  expanded: {
    maxHeight: 500,
  },
  collapsed: {
    maxHeight: 0,
  },
  dummy: {
    marginBottom: 12,
  },
  /* Progress Rows */
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  progressRowLabel: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  progressRowValue: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  smallBarOuter: {
    height: 5,
    backgroundColor: '#333',
    borderRadius: 4,
    overflow: 'hidden',
    marginTop: 4,
  },
  smallBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
  },
  /* Badges */
  badgeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
  },

  badge: {
    borderRadius: 50,
    paddingHorizontal: 8,
    paddingVertical: 3,
    // marginLeft: 'auto',  // overridden inline
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeText: {
    color: '#FFF',
    fontSize: 12,
  },
  statusDefault: {
    fontSize: 12,
    color: '#BDDE36',
    marginLeft: 'auto',
  },
  /* Tooltip */
  tooltip: {
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0,0.9)',
    borderRadius: 8,
    padding: 8,
    width: 220,
    zIndex: 9999,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
  },
  tooltipText: {
    color: '#fff',
    fontSize: 12,
    marginBottom: 6,
    lineHeight: 16,
    fontWeight: '500',
  },
  tooltipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  tooltipLabel: {
    color: '#a1a1a1',
    fontSize: 11,
    fontWeight: '500',
  },
  tooltipValue: {
    color: '#fff',
    fontSize: 11,
    textAlign: 'right',
  },
  /* 3-dot Menu (mini modal) */
  menuContainer: {
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0,0.9)',
    borderRadius: 8,
    padding: 6,
    zIndex: 9999,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
    width: 160,
    borderColor: 'rgba(189,222,54,0.3)',
    borderWidth: 1,
  },
  menuItem: {
    padding: 8,
    borderRadius: 8,
  },
  menuItemHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
  menuItemText: {
    color: '#FFF',
    fontSize: 13,
  },
  menuTrigger: {
    padding: 4,
    borderRadius: 4,
  },
  menuTriggerHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
  centeredText: {
    color: '#000',
    textAlign: 'center',
  },
  

  closeButton: {
    position: 'absolute',
    top: -10,                     // half its height above edge
    right: -10,                   // half its width outside
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#E53E3E',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.5,
    shadowRadius: 4,
    zIndex: 10,
  },
  closeButtonHover: {
    backgroundColor: '#C53030',
  },
  closeButtonText: {
    color: '#FFF',
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 20,
  },
});
